import {
  Component,
  ElementRef,
  Inject,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import {
  MatDialog,
  MatDialogRef,
  MAT_DIALOG_DATA
} from '@angular/material/dialog';
import { MatSelectChange } from '@angular/material/select';
import { Select, Store } from '@ngxs/store';
import { Observable, Subscription } from 'rxjs';
import { Customer } from 'src/app/services/dtos/customer';
import { Laboratory } from 'src/app/services/dtos/laboratory';
import { LaboratoryBasicInfo } from 'src/app/services/dtos/laboratoryBasicInfo';
import { LaboratoryEmailTemplate } from 'src/app/services/dtos/laboratoryEmailTemplate';
import { RightsService } from 'src/app/services/rights.service';
import { Utils } from 'src/app/shared/common.utils';
import { CustomersState } from 'src/app/states/customers-state/customers.state';
import {
  AddUserEmailTemplate,
  UpdateUserEmailTemplate
} from 'src/app/states/customerUsers-state/customerUsers.actions';
import {
  FetchLaboratory,
  SetCurrentLaboratoryId
} from 'src/app/states/laboratories-state/laboratories.actions';
import { LaboratoriesState } from 'src/app/states/laboratories-state/laboratories.state';
import { UserEmailTemplateForm } from '../../../../services/dtos/userEmailTemplateForm';

@Component({
  selector: 'app-user-email-template-dialog',
  templateUrl: './user-email-template-dialog.component.html',
  styleUrls: ['./user-email-template-dialog.component.scss'],
})
export class UserEmailTemplateDialogComponent implements OnInit, OnDestroy {
  @Select(CustomersState.getSelectedCustomer)
  public currentUserCustomer$!: Observable<Customer>;
  @Select(LaboratoriesState.getSelectedLaboratory)
  public laboratory$!: Observable<Laboratory>;
  @Select(LaboratoriesState.getSelectedLaboratoryId)
  public laboratoryId$!: Observable<string>;
  @Select(LaboratoriesState.getLaboratoriesBasicInfo)
  public laboratoriesBasicInfo$!: Observable<LaboratoryBasicInfo[]>;

  @ViewChild('emailTemplateSearch', { static: true })
  private emailTemplateSearch!: ElementRef;

  public currentUserCustomerBasicInfo!: Customer;
  public laboratory!: Laboratory;
  public laboratoryId!: string;
  public laboratoriesBasicInfo!: LaboratoryBasicInfo[];
  public laboratoriesWithEmailTemplates: Laboratory[] = [];  

  public form: UntypedFormGroup = this.fb.group({
    id: [''],
    subject: ['', Validators.required],
    laboratoryEmailTemplateId: ['', Validators.required],
    laboratoryEmailTemplateName: [''],
  });
  public fileChanged = false;
  public filePresentAtInit = false;
  public file: File | undefined;
  public fileName: string | undefined;

  public fullEmailTemplateList: LaboratoryEmailTemplate[] = [];
  
  private subs = new Array<Subscription>();

  public constructor(
    private fb: UntypedFormBuilder,
    public dialogRef: MatDialogRef<UserEmailTemplateDialogComponent>,
    @Inject(MAT_DIALOG_DATA)
    public data: {
      isFromAdminPanel: boolean;
      adminUserLaboratories?: Laboratory[];
      template: UserEmailTemplateForm;
    },
    public dialog: MatDialog,
    private store: Store,
    private accessService: RightsService
  ) {
    this.subs.push(
      this.laboratoriesBasicInfo$.subscribe((laboratories) => {
        if (laboratories) {
          this.laboratoriesBasicInfo = laboratories;
          let labs = laboratories as Laboratory[];
          labs = labs.filter((l) => !!l.emailTemplates);
          for (const lab of labs) {
            const existingLab = this.laboratoriesWithEmailTemplates.find(
              (l) => l.id === lab.id
            );
            const userLabs = this.data.adminUserLaboratories as Laboratory[];
            const isUserLabs = userLabs.find((l) => lab.id === l.id) !== null;
            if (!existingLab && isUserLabs) {
              this.laboratoriesWithEmailTemplates.push({ ...lab });
            }
          }
          this.setFormForTemplate();
        }
      })
    );
    this.subs.push(
      this.laboratoryId$.subscribe((laboratoryId) => {
        if (laboratoryId) {
          this.store.dispatch(new FetchLaboratory(laboratoryId));
        }
      })
    );
    this.subs.push(
      this.laboratory$.subscribe((laboratory: Laboratory) => {
        if (laboratory) {
          this.laboratory = laboratory;
          this.setFormForTemplate();
        }
      })
    );
  }
  
  public get emailTemplateList(): LaboratoryEmailTemplate[] {
    return this.fullEmailTemplateList;
  }

  public get hasMaintainUsersRight(): boolean {
    return (this.data.isFromAdminPanel && this.accessService.canMaintainUsers()) ||
      (!this.data.isFromAdminPanel && this.accessService.canMaintainUsersOfCustomers() && this.accessService.canMaintainCustomers());
  }

  public get labelForClose(): string {
    return Utils.getLabelForClose(this.hasMaintainUsersRight);
  }

  public ngOnInit(): void {
    if (!this.data.isFromAdminPanel) {
      this.subs.push(
        this.currentUserCustomer$.subscribe((user: Customer) => {
          if (user) {
            this.currentUserCustomerBasicInfo = user;
            const currentLab = this.laboratoriesBasicInfo.find(
              (x) => x.id === user.laboratory
            ) as LaboratoryBasicInfo;
            this.store.dispatch(new SetCurrentLaboratoryId(currentLab.id));
            this.store.dispatch(new FetchLaboratory(currentLab.id));
          }
        })
      );
    } else {
      const adminUserLabs = this.data.adminUserLaboratories || [];
      for (const labInfo of adminUserLabs) {
        if (!labInfo.emailTemplates) {
          this.store.dispatch(new SetCurrentLaboratoryId(labInfo.id));
        }
      }
    }
  }

  public ngOnDestroy(): void {
    this.subs.forEach((s) => s.unsubscribe());
    this.laboratoriesWithEmailTemplates = [];
  }

  public onEmailTemplateSelect(
    emailTemplateChangeEvent: MatSelectChange
  ): void {
    this.form.controls.laboratoryEmailTemplateName.setValue(
      emailTemplateChangeEvent.value
    );

    const contextLabId = this.emailTemplateList?.find(
      (e) => e?.name === emailTemplateChangeEvent.value
    )?.id;

    this.form.controls.laboratoryEmailTemplateId.setValue(contextLabId);
    this.setDefaultEmailTemplateSubject(contextLabId);
  }

  public emailTemplateSelectToggled(): void {
    this.emailTemplateSearch.nativeElement.value = '';
  }

  public isEmailTemplateInFilter(
    emailTemplate: LaboratoryEmailTemplate
  ): boolean {
    const query = this.emailTemplateSearch.nativeElement.value;

    return this.emailTemplateList
      ?.filter((x) => x.name.toLowerCase().includes(query.toLowerCase()))
      .some((x) => x === emailTemplate);
  }

  public displayEmailTemplate(emailTemplate: LaboratoryEmailTemplate): string {
    return emailTemplate?.name;
  }

  public saveDetails(): void {
    const templateForm = this.form.value as UserEmailTemplateForm;
    templateForm.customerId =
      this.data.template.customerId !== ''
        ? this.data.template.customerId
        : null;
    templateForm.userId = this.data.template.userId;

    if (this.data.template.id) {
      this.store.dispatch(new UpdateUserEmailTemplate(templateForm));
    } else {
      this.store.dispatch(new AddUserEmailTemplate(templateForm));
    }

    this.dialogRef.close();
  }

  public cancel(): void {
    this.dialogRef.close();
  }

  public canSave(): boolean {
    return this.form.valid;
  }

  private removeExistingEmailTemplates(
    laboratoryEmailTemplates: LaboratoryEmailTemplate[]
  ): LaboratoryEmailTemplate[] {
    const emailTemplates = laboratoryEmailTemplates.filter(
      (e) =>
        e.id === this.data.template.laboratoryEmailTemplateId ||
        this.data.template.currentEmailTemplates.find(
          (e2) => e2.laboratoryEmailTemplateId === e.id
        ) == null
    );

    return emailTemplates.filter(
      (value, index, self) => index === self.findIndex((t) => t.id === value.id)
    );
  }

  private refreshEmailTemplatesList(): void {
    if (!this.data.isFromAdminPanel) {
      this.fullEmailTemplateList =
        this.laboratory && this.laboratory.emailTemplates
          ? this.removeExistingEmailTemplates([
              ...this.laboratory.emailTemplates,
            ])
          : [];
    } else {
      const adminUserLabs = this.data.adminUserLaboratories?.map(l => l.id) as string[];
      const userLabsEmails = this.laboratoriesWithEmailTemplates.filter(
        (l) => adminUserLabs.indexOf(l.id) > -1
      );
      const data = userLabsEmails
        ?.map((l: Laboratory) => l?.emailTemplates)
        .reduce((pre, cur) => pre.concat(cur), []);

      if (data && data.length === 0) {
        this.fullEmailTemplateList = [];
        return;
      }

      this.fullEmailTemplateList = this.removeExistingEmailTemplates(
        [...data.filter((d) => !!d)]
      );
    }
  }
  
  private setDefaultEmailTemplateSubject(
    emailTemplateId: string | undefined
  ): void {
    const defaultSubject =
      this.emailTemplateList?.find((e) => e?.id === emailTemplateId)?.subject ||
      '';
    this.form.controls.subject.setValue(defaultSubject);
  }

  private setFormForTemplate(): void {
    this.form.patchValue({
      id: this.data.template?.id,
      laboratoryEmailTemplateId: this.data.template?.laboratoryEmailTemplateId,
      laboratoryEmailTemplateName: this.emailTemplateList?.find(
        (e) => e?.id === this.data.template?.laboratoryEmailTemplateId
      )?.name,
      subject: this.data.template?.subject,
    });

    this.form.markAsUntouched();
    this.form.markAsPristine();
    this.refreshEmailTemplatesList();

    if (!this.hasMaintainUsersRight) {
      this.form.disable();
    }
  }
}
